import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FtDaterangeCalendar, FtDaterangeOptions } from '../ft-daterange-calendar/ft-daterange-calendar';

/**
 * Native port of the AngularJS `ftDateRange` ATTRIBUTE directive
 * (account-v1/src/components/common/date-range/dateRange.js lines 10-76).
 *
 * Unlike the contest `ft-date-range-picker-ng` COMPONENT (which renders its OWN
 * `.ft-dropdown.date-range` markup), this attaches {@link FtDaterangeCalendar} to WHATEVER element
 * it sits on — exactly as v1 did — so the pre-stay criteria editor can put it on the EMPTY
 * `<a class="action btn-add-item">` add-trigger (whose whole appearance is the `+` glyph) AND on the
 * per-range `<div class="ft-dropdown">` edit-dropdown, each keeping its own projected content. It
 * MUST stay a directive: turning it into a component with its own markup would replace that `+`
 * button with a dropdown box.
 *
 * ⚠ The host must not gain an Angular `[class.active]` binding: the calendar toggles `active` on the
 * host imperatively, as the plugin did, and the two would fight. Both current hosts use a static
 * `class` only.
 *
 * Verbatim v1 semantics: (re)`initialize()` on init + whenever `startDate`/`endDate`/`options`
 * change (the v1 `$watch`es), ESC-to-hide, the change callback run inside NgZone so the host's
 * `(dateRangeChanged)` propagates, `options.convertToNative` choosing date objects vs dayjs, and
 * teardown of the body-appended calendar in ngOnDestroy.
 *
 * The pre-stay season ranges this drives are YEAR-LESS (`format: 'MMMM D'`), which
 * FtDaterangeCalendar detects from the format and handles explicitly — see its `yearless` notes for
 * the two live-data bugs that behaviour fixes.
 */
@Directive({
  selector: '[ftDateRangeNg]',
  host: {
    '(document:keydown)': 'onDocumentKey($event)',
    '(document:keypress)': 'onDocumentKey($event)',
  },
})
export class FtDateRangeDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** v1 scope `startDate` (=?) — the picker's current start, or undefined for "add new". */
  @Input() startDate: any;
  /** v1 scope `endDate` (=?). */
  @Input() endDate: any;
  /** v1 scope `options` (@? then `$eval`'d) — here passed as the object directly. */
  @Input() options: FtDaterangeOptions;
  /** v1 `notifyDateRangeChanged` (&onDateRangeChanged) — emits `{ start, end }`. */
  @Output() dateRangeChanged = new EventEmitter<{ start: any; end: any }>();

  private readonly el = inject(ElementRef);
  private readonly zone = inject(NgZone);
  private picker: FtDaterangeCalendar | null = null;
  private viewReady = false;

  protected onDocumentKey(event: KeyboardEvent): void {
    if (event.which === 27 || event.keyCode === 27) this.picker?.hide();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // v1: re-initialize when startDate/endDate become defined, or when options change.
    if (!this.viewReady) return;
    const start = changes['startDate'];
    const end = changes['endDate'];
    if ((start && start.currentValue !== undefined) || (end && end.currentValue !== undefined) || changes['options']) {
      this.initialize();
    }
  }

  ngOnDestroy(): void {
    this.picker?.remove();
    this.picker = null;
  }

  /**
   * v1 `initialize()`. Re-seeds the EXISTING calendar rather than building a second one — v1 rebuilt
   * the jQuery picker from scratch here and leaked its body-appended container on every re-init.
   */
  private initialize(): void {
    const options: FtDaterangeOptions = { ...(this.options || {}) };
    options.startDate = this.startDate || options.startDate;
    options.endDate = this.endDate || options.endDate;

    const emit = (start: any, end: any) => {
      // Runs from a DOM listener, i.e. outside Angular's zone (v1 wrapped it in scope.$apply).
      this.zone.run(() => {
        this.startDate = start;
        this.endDate = end;
        this.dateRangeChanged.emit({
          start: options.convertToNative ? start.toDate() : start,
          end: options.convertToNative ? end.toDate() : end,
        });
      });
    };

    if (this.picker) this.picker.setOptions(options, emit);
    else this.picker = new FtDaterangeCalendar(this.el.nativeElement, options, emit);
  }
}
